import React from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, MapPin, ArrowRight, BarChart3, Plug, Map as MapIcon, LineChart, ListChecks, Instagram, Trophy, Users, Gift, Sparkles } from 'lucide-react';
import PricePreview from '../components/PricePreview';

const IMG_WRAP = 'https://static.prod-images.emergentagent.com/jobs/489873ad-582b-4a32-a22f-601ab3398056/images/458f96e4a223d84c32399bf5a0eeaab7e80e69085a182fe77f9a3acf7a4d5518.jpeg';
const IMG_FOLLOW = 'https://static.prod-images.emergentagent.com/jobs/489873ad-582b-4a32-a22f-601ab3398056/images/498b346b9e222a3b06901e003bfff17f48748271d3a84ef10acef37cf13c27fb.jpeg';
const IMG_GROWTH = 'https://static.prod-images.emergentagent.com/jobs/489873ad-582b-4a32-a22f-601ab3398056/images/a8f33b79a2294b4c2d6a6195c36a3d9626f54593ef5f390633649e9c5fdb7fd5.jpeg';

const ForAdvertisers = () => {
  const advertiserSteps = [
    { step: 1, title: 'Contact Us', description: 'Fill out our get started form to discuss your advertising needs and target areas in Columbus.' },
    { step: 2, title: 'Design Your Ad', description: 'Work with us to create an eye-catching design for your full car wrap advertisement.' },
    { step: 3, title: 'Get Your Custom Quote', description: 'We will provide a personalized consultation and package tailored to your business needs.' },
    { step: 4, title: 'Launch Campaign', description: 'Your ad hits the streets of Columbus reaching thousands of people daily; track your results on the advertiser dashboard, explained below.' }
  ];

  const socialSteps = [
    {
      n: 1,
      img: IMG_WRAP,
      alt: 'Car wrapped in navy advertising vinyl with a gold Instagram handle and QR code on the rear panel',
      icon: Instagram,
      title: 'Your Instagram handle rides on the wrap',
      body: 'We put your @handle — plus the sweepstakes prize and a scannable code — right on the vehicle. Every person who sees the car gets a reason to pull out their phone.',
      testid: 'social-step-1',
    },
    {
      n: 2,
      img: IMG_FOLLOW,
      alt: 'Phone showing a follow button and a comment tagging two friends',
      icon: Users,
      title: 'They follow you and tag two friends',
      body: 'To enter, they follow your page and comment on your pinned post tagging two friends. Each tag pulls more local people onto your page — the entry itself is the marketing.',
      testid: 'social-step-2',
    },
    {
      n: 3,
      img: IMG_GROWTH,
      alt: 'Follower growth chart next to a gift box and raffle ticket',
      icon: Trophy,
      title: 'You keep the followers. One entrant wins the prize.',
      body: 'You pick the prize; Advecti runs the drawing every 30 days and hands you the winner. The real payoff is the audience you keep — a warm local following you can market to for free, forever.',
      testid: 'social-step-3',
    },
  ];

  return (
    <div className="bg-cream-light" data-testid="for-advertisers-page">
      {/* Hero */}
      <section className="hero-gradient py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-navy/10 px-4 py-2 rounded-full mb-6">
              <Megaphone className="text-navy" size={20} />
              <span className="text-navy font-medium">For Advertisers</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy leading-tight">
              Let Your Brand Take the <span className="text-gold">Spotlight</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-navy/70">
              Mobile advertising that doesn't just get seen — it grows your following.
              Professional car wraps on the streets of Columbus, with social attribution built in.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/get-started"
                className="inline-flex items-center justify-center gap-2 bg-navy text-cream px-8 py-4 rounded-full text-lg font-semibold hover:bg-navy-dark transition-all pulse-cta"
                data-testid="advertiser-cta"
              >
                Start Advertising
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY WE'RE THE BEST: Social Media Attribution ───────────────── */}
      <section className="py-16 md:py-24 bg-white" data-testid="social-attribution-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: 'rgba(201, 162, 39, 0.15)' }}>
              <Sparkles className="text-gold" size={18} />
              <span className="text-navy font-semibold text-sm tracking-wide uppercase">Why We're the Best</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-navy leading-tight">
              Social Media <span className="text-gold">Attribution</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-navy/70">
              Every other wrap company hands you an impression estimate and hopes you feel good
              about it. We put your Instagram on the car and run a sweepstakes off it — so the
              people who see your wrap don't just notice you, they <strong className="text-navy">follow</strong> you.
              New followers are the proof the campaign worked, and they're yours to keep.
            </p>
          </div>

          {/* 3-step flow */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {socialSteps.map((s) => (
              <div key={s.n} className="group" data-testid={s.testid}>
                <div className="rounded-2xl overflow-hidden bg-cream border border-navy/5 shadow-sm transition-transform duration-300 group-hover:-translate-y-1">
                  <img src={s.img} alt={s.alt} className="w-full aspect-square object-cover" loading="lazy" />
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-navy text-cream flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {s.n}
                  </span>
                  <s.icon className="text-gold" size={20} />
                </div>
                <h3 className="mt-3 text-xl font-bold text-navy">{s.title}</h3>
                <p className="mt-2 text-navy/70 text-sm md:text-base">{s.body}</p>
              </div>
            ))}
          </div>

          {/* What we report */}
          <div className="mt-16 bg-navy rounded-3xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full" style={{ backgroundColor: 'rgba(201, 162, 39, 0.12)' }} />
            <div className="relative">
              <h3 className="text-2xl md:text-3xl font-bold text-cream">
                How we measure success
              </h3>
              <p className="mt-3 text-cream max-w-2xl text-sm md:text-base opacity-80">
                No spreadsheets, no revenue disclosure, no guesswork. Your dashboard tracks the
                three numbers that come straight from the wrap.
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="rounded-2xl p-6 border" style={{ backgroundColor: 'rgba(245, 240, 220, 0.06)', borderColor: 'rgba(245, 240, 220, 0.18)' }} data-testid="social-metric-followers">
                  <Users className="text-gold mb-3" size={24} />
                  <p className="text-3xl font-bold text-cream">New Followers</p>
                  <p className="text-cream text-sm mt-2 opacity-75">
                    Daily follower snapshots of your page from the day the wrap hits the road.
                  </p>
                </div>
                <div className="rounded-2xl p-6 border" style={{ backgroundColor: 'rgba(245, 240, 220, 0.06)', borderColor: 'rgba(245, 240, 220, 0.18)' }} data-testid="social-metric-entries">
                  <ListChecks className="text-gold mb-3" size={24} />
                  <p className="text-3xl font-bold text-cream">Entries &amp; Tags</p>
                  <p className="text-cream text-sm mt-2 opacity-75">
                    Every sweepstakes comment is logged automatically, including how many friends
                    each entrant tagged.
                  </p>
                </div>
                <div className="rounded-2xl p-6 border" style={{ backgroundColor: 'rgba(245, 240, 220, 0.06)', borderColor: 'rgba(245, 240, 220, 0.18)' }} data-testid="social-metric-prize">
                  <Gift className="text-gold mb-3" size={24} />
                  <p className="text-3xl font-bold text-cream">Your Prize</p>
                  <p className="text-cream text-sm mt-2 opacity-75">
                    You choose what to give away. Advecti runs the drawing every 30 days and sends
                    you the winner's handle.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/get-started"
                  className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-3.5 rounded-full font-bold hover:brightness-95 transition-all"
                  data-testid="social-attribution-cta"
                >
                  Start a Campaign
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/bloom/rules"
                  className="inline-flex items-center justify-center gap-2 border text-cream px-7 py-3.5 rounded-full font-semibold hover:opacity-80 transition-opacity" style={{ borderColor: 'rgba(245, 240, 220, 0.35)' }}
                  data-testid="social-attribution-rules-link"
                >
                  Read the sweepstakes rules
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Advertisers Steps */}
      <section className="py-16 bg-cream" data-testid="advertiser-steps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12 justify-center">
            <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center">
              <Megaphone className="text-cream" size={28} />
            </div>
            <h2 className="text-3xl font-bold text-navy">Advertiser Process</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advertiserSteps.map((item, index) => (
              <div key={item.step} className="relative">
                <div className="card-hover bg-white p-6 rounded-2xl h-full">
                  <div className="w-10 h-10 bg-navy text-cream rounded-full flex items-center justify-center font-bold text-lg mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-navy mb-2">{item.title}</h3>
                  <p className="text-navy/70">{item.description}</p>
                </div>
                {index < advertiserSteps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-navy/30" size={24} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live price preview */}
      <PricePreview />

      {/* Inside the Advertiser Dashboard */}
      <section className="py-16 bg-cream" data-testid="advertiser-dashboard-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-navy/10 px-4 py-2 rounded-full mb-4">
              <BarChart3 className="text-navy" size={20} />
              <span className="text-navy font-medium">Advertiser Dashboard</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-navy">
              See Your Campaign in Real Time
            </h2>
            <p className="mt-4 text-base md:text-lg text-navy/70 max-w-3xl mx-auto">
              Once your campaign is live, you get private dashboard access where every impression,
              every session, and every new follower is measured — not estimated.
            </p>
          </div>

          {/* Feature cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {/* Social attribution tab */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-navy/5">
              <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center mb-4">
                <Instagram className="text-cream" size={22} />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3">Social Attribution, Live</h3>
              <p className="text-navy/70 mb-3">
                Your own tab showing follower growth since launch, every sweepstakes entry with the
                entrant's handle, friend tags counted, and the current drawing cycle.
              </p>
              <p className="text-navy/70">
                Connect your Instagram once and it updates itself daily — nothing to upload,
                nothing to reconcile.
              </p>
            </div>

            {/* Sessions tab */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-navy/5">
              <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center mb-4">
                <ListChecks className="text-cream" size={22} />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3">Every Driving Session, Itemized</h3>
              <p className="text-navy/70 mb-3">
                Open the <strong>Sessions tab</strong> and you'll see each completed shift:
                driver, date, hours driven, impressions, and estimated cost — line by line.
                No black box, no monthly summary that hides the details.
              </p>
              <p className="text-navy/70">
                Click any session to expand its route map and zone breakdown so you know
                exactly where your impressions came from.
              </p>
            </div>

            {/* Routes / Zone Breakdown */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-navy/5">
              <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center mb-4">
                <MapIcon className="text-cream" size={22} />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3">Where Your Impressions Came From</h3>
              <p className="text-navy/70 mb-3">
                Every session includes the driver's actual GPS route plotted on a map of Columbus,
                with each segment labeled by the geofenced zone it passed through —
                <em> Downtown Columbus, Short North, Polaris, Easton</em>, and so on.
              </p>
              <p className="text-navy/70">
                You'll always know how many impressions came from your priority neighborhoods
                versus the broader I-270 metro area.
              </p>
            </div>

            {/* Impressions analytics */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-navy/5">
              <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center mb-4">
                <BarChart3 className="text-cream" size={22} />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-3">Impressions You Can Audit</h3>
              <p className="text-navy/70 mb-3">
                Daily impressions split by targeted (your priority zones) versus the wider metro,
                with a day-by-day table of sessions and hours driven.
              </p>
              <p className="text-navy/70">
                Counts come from real GPS time inside real geofences — not a traffic-study
                multiplier applied to a guess.
              </p>
            </div>
          </div>

          {/* Dashboard preview screenshots */}
          <div className="mb-12" data-testid="dashboard-preview-section">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-navy mb-2">A Peek Inside the Dashboard</h3>
              <p className="text-navy/60 max-w-2xl mx-auto">
                Real screenshots from a live advertiser account — impressions and daily breakdowns
                all in one place.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <figure className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden" data-testid="preview-impressions-chart">
                <img
                  src="https://customer-assets.emergentagent.com/job_489873ad-582b-4a32-a22f-601ab3398056/artifacts/jf3cuoaj_image.png"
                  alt="Impressions analytics with stacked bar chart showing daily targeted vs non-targeted impressions"
                  className="w-full h-auto block"
                  loading="lazy"
                />
                <figcaption className="px-6 py-4 border-t border-navy/5">
                  <p className="text-navy font-semibold">Impressions Analytics</p>
                  <p className="text-navy/60 text-sm mt-1">
                    Daily impressions split by targeted (mini-zone) vs non-targeted with a 14-day stacked bar chart.
                  </p>
                </figcaption>
              </figure>

              <figure className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden" data-testid="preview-daily-breakdown">
                <img
                  src="https://customer-assets.emergentagent.com/job_489873ad-582b-4a32-a22f-601ab3398056/artifacts/pg28buv4_image.png"
                  alt="Daily breakdown table listing total, targeted, non-targeted impressions, sessions and hours per day"
                  className="w-full h-auto block"
                  loading="lazy"
                />
                <figcaption className="px-6 py-4 border-t border-navy/5">
                  <p className="text-navy font-semibold">Daily Breakdown</p>
                  <p className="text-navy/60 text-sm mt-1">
                    Itemized day-by-day numbers — total impressions, targeted, non-targeted, session count, and hours driven.
                  </p>
                </figcaption>
              </figure>
            </div>
          </div>

          {/* Stat strip */}
          <div className="bg-navy rounded-2xl p-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <p className="text-4xl font-bold text-cream">Live</p>
              <p className="text-cream mt-1 text-sm">Impressions update the moment a session ends</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-cream">Per-Zone</p>
              <p className="text-cream mt-1 text-sm">Breakdown of where every impression came from</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-cream">Followers</p>
              <p className="text-cream mt-1 text-sm">Real people who followed your page because of the wrap</p>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/advertiser-login"
              className="inline-flex items-center gap-2 bg-navy text-cream px-8 py-4 rounded-full text-lg font-semibold hover:bg-navy-dark transition-all"
              data-testid="advertiser-dashboard-login-cta"
            >
              Sign in to Your Dashboard
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── OPTIONAL: sales & revenue tracking ─────────────────────────── */}
      <section className="py-14 bg-white border-t border-navy/5" data-testid="optional-sales-tracking-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-navy/40">
            Optional: Sales &amp; Revenue Tracking
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-navy">
            Want revenue lift too? Flip it on.
          </h2>
          <p className="mt-3 text-navy/60 text-sm md:text-base max-w-3xl">
            Social attribution is how we measure every campaign. Revenue and website lift are
            <strong className="text-navy"> completely optional</strong> — off by default, and you can
            switch them on or off any time from your dashboard. Nothing about your sales data is
            required to run with us.
          </p>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-cream rounded-2xl p-7 border border-navy/5" data-testid="optional-attribution-card">
              <div className="w-11 h-11 bg-navy/10 rounded-full flex items-center justify-center mb-4">
                <LineChart className="text-navy" size={20} />
              </div>
              <h3 className="text-xl font-bold text-navy mb-2">Before vs. After Lift</h3>
              <p className="text-navy/70 text-sm">
                If you opt in, we capture a 90-day baseline of your traffic and purchase volume
                before launch, then compare the same metrics while the campaign runs. The lift you
                see is the actual difference — not a vendor vanity metric.
              </p>
            </div>

            <div className="bg-cream rounded-2xl p-7 border border-navy/5" data-testid="optional-integrations-card">
              <div className="w-11 h-11 bg-navy/10 rounded-full flex items-center justify-center mb-4">
                <Plug className="text-navy" size={20} />
              </div>
              <h3 className="text-xl font-bold text-navy mb-2">One-Click Connections</h3>
              <p className="text-navy/70 text-sm">
                <strong>Shopify, Square, Stripe</strong>, or the zero-config <strong>Advecti Pixel</strong>
                {' '}for site visits. There's also a manual entry form if your booking app has no API.
                Connect nothing and your campaign still reports fully.
              </p>
            </div>
          </div>

          <figure className="mt-8 bg-cream rounded-2xl border border-navy/5 overflow-hidden" data-testid="preview-conversion-tracking">
            <img
              src="https://customer-assets.emergentagent.com/job_489873ad-582b-4a32-a22f-601ab3398056/artifacts/f8ngeqt7_Screenshot%202026-05-15%20194434.png"
              alt="Optional conversion tracking dashboard showing total revenue lift and a daily revenue chart with campaign-start marker"
              className="w-full h-auto block"
              loading="lazy"
            />
            <figcaption className="px-6 py-4 border-t border-navy/5">
              <p className="text-navy font-semibold">Conversions tab (opt-in)</p>
              <p className="text-navy/60 text-sm mt-1">
                Only appears in your dashboard when sales tracking is switched on.
              </p>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-cream mb-4">
            Ready to Advertise?
          </h2>
          <p className="text-base md:text-lg text-cream mb-8">
            Let's put your brand on the streets of Columbus — and your page in front of the city.
          </p>
          <Link
            to="/get-started"
            className="inline-flex items-center gap-3 bg-cream text-navy px-10 py-5 rounded-full text-xl font-bold hover:bg-cream-light transition-all pulse-cta"
            data-testid="advertiser-cta-final"
          >
            Get Started Now
          </Link>
          <p className="mt-6 text-cream">
            <MapPin className="inline mr-2" size={16} />
            Serving Businesses in Columbus, Ohio
          </p>
        </div>
      </section>
    </div>
  );
};

export default ForAdvertisers;
